import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { Read } from 'services/banking';
import { Title } from 'ui/styled';
import { normalizeResponse } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';
import { OmniActionButton } from './OmniActionButton';

export default function BankingNegocios() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, setModal, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Read('?filters[status]=motor');
      const data = normalizeResponse(result);
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
      const next = sorted.map((item) => ({
        ...item,
        accepted: (item.bancos || []).find((f) => f.aceito),
      }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);
  const columns = useMemo(() => {
    const baseColumns = [
      { title: 'Id', ref: 'id_proposal', sortable: true, align: 'center' },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      {
        title: 'SLA',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      { title: 'Taxa', ref: 'taxa' },
      { title: 'Prazo', ref: 'prazo' },
      { title: 'PMT', ref: 'valor_parcelas' },
      {
        title: '1º Vencimento',
        ref: 'data_vencimento',
        renderCell: ({ row }) =>
          formatadores.TIMESTAMP_TO_BR(row.data_vencimento),
      },
      {
        title: 'TAG',
        ref: 'status',
        renderCell: ({ row }) => (
          <OmniActionButton
            row={row}
            user={user}
            setModal={setModal}
            init={init}
          />
        ),
      },
      // Inserir coluna "Espelho da Proposta" condicionalmente
      ...(user?.administrador
        ? [
            {
              title: 'Espelho da Proposta',
              ref: 'espelho',
              renderCell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={() =>
                      navigate(`proposals/financing/${row?.id_proposal}`)
                    }
                    color="blue"
                    nospace
                    mbutton
                    small
                  >
                    Visualizar
                  </Button>
                </div>
              ),
            },
          ]
        : []),
    ];
    return baseColumns;
  }, [user, navigate, init, setModal]);
  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.id,
      date: a?.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.createdAt),
      distributor: normalizeName(a?.distribuidor?.trade_name) || '-',
      distributorId: a?.distribuidor?.id,
      integrator:
        normalizeName(a?.proposta_financeira?.integrador?.nome_fantasia) || '-',
      customer: normalizeName(a?.cliente?.nome_completo) || '-',
      value: parseCurrency(
        a?.proposta_financeira?.informacoes_projeto?.custo_total_projeto
      ),
      status: normalizeName(a?.status || ''),
      id_proposal: a?.proposta_financeira?.id,
      proposta_financeira: a?.proposta_financeira,
      taxa: `${a?.distribuidor?.taxas[0]?.perc_taxa}%`,
      taxas: a?.distribuidor?.taxas?.find((t) => t.padrao) || null,
      taxas_all: a?.distribuidor?.taxas || null,
      data_vencimento: !a?.proposta_financeira?.propostas_banco[0]?.data
        ? '--'
        : a?.proposta_financeira?.propostas_banco[0]?.data,
      valor_parcelas: parseCurrency(
        a?.proposta_financeira?.propostas_banco[0]?.valor_parcelas
      ),
      prazo: a?.proposta_financeira?.propostas_banco[0]?.parcelas,
      documento_bureau: a?.documento_bureau,
    }));
  }, [registers]);
  //console.log(registers);
  useEffect(() => init(), [init, tick]);

  return (
    <ContainerAuthenticated>
      <Title>Banking</Title>
      <TableWrapper title="Motor" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
